import React, { useState, useEffect } from 'react';
import PortsManagementHeader from './PortsManagementHeader';
import PortsTable from './PortsTable';
import { Plus } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { CommonButton } from '@/components/ui/common-button';
import EditPortDialog from './editports/EditPortDialog';
import AddPortDialog from './addports/AddPortDialog';
import ViewPortDialog from './viewports/ViewPortDialog';
import DeletePortDialog from './deleteports/DeletePortDialog';
import portService from '@/services/portService';
import { FullScreenLoader, TableBlurLoader } from '@/components/ui/loader';
import { usePagination } from '@/hooks/usePagination';

const PortsManagementPage = () => {
  const [ports, setPorts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [editPortDialogOpen, setEditPortDialogOpen] = useState(false);
  const [addPortDialogOpen, setAddPortDialogOpen] = useState(false);
  const [viewPortDialogOpen, setViewPortDialogOpen] = useState(false);
  const [deletePortDialogOpen, setDeletePortDialogOpen] = useState(false);
  const [selectedPort, setSelectedPort] = useState(null);
  const [selectedPortDetails, setSelectedPortDetails] = useState(null);
  const [portDetailsLoading, setPortDetailsLoading] = useState(false);
  const [totalPorts, setTotalPorts] = useState(0);
  
  const { currentPage, pageSize, setPage } = usePagination({ defaultPage: 1, defaultPageSize: 10 });
  
  const { toast } = useToast();

  useEffect(() => {
    fetchPorts();
  }, [currentPage, pageSize]);

  const fetchPorts = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * pageSize;
      const portData = await portService.getPorts(skip, pageSize);
      
      let portsData, totalCount;
      
      if (portData && typeof portData === 'object' && Array.isArray(portData.data)) {
        portsData = portData.data;
        totalCount = portData.total || portData.count || portsData.length;
      } else if (Array.isArray(portData)) {
        portsData = portData;
        totalCount = portData.length < pageSize ? skip + portData.length : skip + portData.length + 1;
      } else if (portData && typeof portData === 'object' && Array.isArray(portData.ports)) {
        portsData = portData.ports;
        totalCount = portData.total || portData.count || portsData.length;
      } else {
        portsData = [];
        totalCount = 0;
      }
      
      const transformedPorts = portsData.map(port => ({
        id: port.id,
        name: port.port_name,
        code: port.port_code,
        porttype: port.porttype,
        address: port.address,
        contactPerson: port.contact_person,
        phoneNumber: port.phone_number,
        status: port.is_active ? 'Active' : 'Inactive',
        createdAt: port.created_at ? new Date(port.created_at).toLocaleDateString() : 'N/A',
        coordinates: {
          lat: port.latitude,
          lng: port.longitude
        }
      }));
      
      setPorts(transformedPorts);
      setTotalPorts(totalCount);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch ports:', err);
      setError('Failed to load ports');
      toast({
        title: "Error",
        description: "Failed to load ports. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  };

  const fetchPortById = async (portId) => {
    try {
      setPortDetailsLoading(true);
      const portData = await portService.getPortById(portId);
      const transformedPort = {
        id: portData.id,
        name: portData.port_name,
        code: portData.port_code,
        porttype: portData.porttype,
        address: portData.address,
        contactPerson: portData.contact_person,
        phoneNumber: portData.phone_number,
        status: portData.is_active ? 'Active' : 'Inactive',
        createdAt: portData.created_at ? new Date(portData.created_at).toLocaleDateString() : 'N/A',
        coordinates: {
          lat: portData.latitude,
          lng: portData.longitude
        }
      };
      setSelectedPortDetails(transformedPort);
      return transformedPort;
    } catch (err) {
      console.error('Failed to fetch port details:', err);
      toast({
        title: "Error",
        description: "Failed to load port details. Please try again.",
        variant: "destructive",
      });
      return null;
    } finally {
      setPortDetailsLoading(false);
    }
  };

  const handleAddPort = () => {
    setAddPortDialogOpen(true);
  };

  const handleViewPort = async (portId) => {
    const portDetails = await fetchPortById(portId);
    if (portDetails) {
      setSelectedPortDetails(portDetails);
      setViewPortDialogOpen(true);
    }
  };

  const handleEditPort = async (portId) => {
    const portDetails = await fetchPortById(portId);
    if (portDetails) {
      setSelectedPortDetails(portDetails);
      setEditPortDialogOpen(true);
    }
  };

  const handleDeletePort = (portId) => {
    const port = ports.find(p => p.id === portId);
    setSelectedPort(port);
    setDeletePortDialogOpen(true);
  };

  const handleCreatePort = async (newPort) => {
    try {
      const createdPort = await portService.createPort(newPort);
      const transformedPort = {
        id: createdPort.id,
        name: createdPort.port_name,
        code: createdPort.port_code,
        porttype: createdPort.porttype,
        address: createdPort.address,
        contactPerson: createdPort.contact_person,
        phoneNumber: createdPort.phone_number,
        status: createdPort.is_active ? 'Active' : 'Inactive',
        createdAt: createdPort.created_at ? new Date(createdPort.created_at).toLocaleDateString() : 'N/A',
        coordinates: {
          lat: createdPort.latitude,
          lng: createdPort.longitude
        }
      };
      setPorts([transformedPort, ...ports]);
      setAddPortDialogOpen(false);
      toast({
        title: "Port Added",
        description: `${newPort.port_name} has been successfully added.`,
      });
      
      return createdPort;
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to add port. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleUpdatePort = async (updatedPort) => {
    try {
      const portData = {
        port_name: updatedPort.port_name,
        port_code: updatedPort.port_code,
        porttype: updatedPort.porttype, 
        latitude: parseFloat(updatedPort.latitude),
        longitude: parseFloat(updatedPort.longitude),
        address: updatedPort.address,
        contact_person: updatedPort.contact_person,
        phone_number: updatedPort.phone_number,
        is_active: updatedPort.is_active
      };
      
      const response = await portService.updatePort(updatedPort.id, portData);
      
      const transformedPort = {
        id: response.id,
        name: response.port_name,
        code: response.port_code,
        porttype: response.porttype, 
        address: response.address,
        contactPerson: response.contact_person,
        phoneNumber: response.phone_number,
        status: response.is_active ? 'Active' : 'Inactive',
        createdAt: response.created_at ? new Date(response.created_at).toLocaleDateString() : 'N/A',
        coordinates: {
          lat: response.latitude,
          lng: response.longitude
        }
      };
      
      setPorts(ports.map(port => port.id === updatedPort.id ? transformedPort : port));
      setEditPortDialogOpen(false);
      setSelectedPortDetails(null);
      toast({
        title: "Port Updated",
        description: `${transformedPort.name} has been successfully updated.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to update port. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleConfirmDeletePort = async (portToDelete) => {
    try {
      const response = await portService.deletePort(portToDelete.id);
      setPorts(ports.filter(port => port.id !== portToDelete.id));
      setDeletePortDialogOpen(false);
      setSelectedPort(null);
      toast({
        title: "Port Deleted",
        description: `${portToDelete.name} has been successfully deleted.`,
      });
    } catch (error) {
      console.error('Delete port error:', error);
      toast({
        title: "Error",
        description: `Failed to delete port: ${error.message || 'Please try again.'}`,
        variant: "destructive",
      });
    }
  };

  if (isInitialLoad) {
    return <FullScreenLoader isLoading={isInitialLoad} />;
  }

  if (error) {
    return <div className="flex justify-center items-center h-64 text-red-500">{error}</div>
  }
  
  const totalPages = Math.ceil(totalPorts / pageSize);

  return (
    <div>
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-lg font-semibold tracking-tight text-foreground">Ports Management</h1>
          <p className="text-xs text-muted-foreground">Configure operational hub terminal zones and coordinate bounds.</p>
        </div>
        <div className="flex items-center space-x-2">
          <CommonButton onClick={handleAddPort} variant="default" icon={Plus}>
            Add Port
          </CommonButton>
        </div>
      </div>
      
      <PortsManagementHeader ports={ports} />
      
      <TableBlurLoader 
        isLoading={loading} 
        className="mt-4"
      >
        <div className="border border-border p-0.5 rounded-[16px] bg-card w-full transition-all transform" style={{boxShadow: '0px 2px 10px 0px hsl(var(--border) / 0.1)'}}>
          <div className="border border-border py-4 rounded-[16px] bg-background">
            {ports.length > 0 ? (
              <PortsTable 
                ports={ports} 
                onViewPort={handleViewPort}
                onEditPort={handleEditPort}
                onDeletePort={handleDeletePort}
                currentPage={currentPage}
                totalPages={totalPages}
                totalPorts={totalPorts}
                pageSize={pageSize}
                onPageChange={setPage}
              />
            ) : (
              <div className="flex justify-center items-center h-64 text-muted-foreground">
                {loading ? "" : "No data available"}
              </div>
            )}
          </div>
        </div>
      </TableBlurLoader>

      <EditPortDialog
        open={editPortDialogOpen}
        onOpenChange={(open) => {
          setEditPortDialogOpen(open);
          if (!open) setSelectedPortDetails(null);
        }}
        port={selectedPortDetails}
        onUpdatePort={handleUpdatePort}
      />
      
      <AddPortDialog
        open={addPortDialogOpen}
        onOpenChange={setAddPortDialogOpen}
        onAddPort={handleCreatePort}
      />
      
      <ViewPortDialog
        open={viewPortDialogOpen}
        onOpenChange={(open) => {
          setViewPortDialogOpen(open);
          if (!open) setSelectedPortDetails(null);
        }}
        port={selectedPortDetails}
      />
      
      {selectedPort && (
        <DeletePortDialog
          open={deletePortDialogOpen}
          onOpenChange={setDeletePortDialogOpen}
          port={selectedPort}
          onDeletePort={handleConfirmDeletePort}
        />
      )}
    </div>
  );
};

export default PortsManagementPage;